import React, { useState } from 'react';
import { View, ScrollView, Pressable, Linking, Platform, StyleSheet } from 'react-native';
import { Link } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import Text from '@/components/Typography/Text';
import Logo from '@/components/Icons/Logo';
import Icon from '@/components/Icon';

// The mobile welcome design, with room for a form in a resizable desktop window.
export default function DesktopAuthShell({ children, title, subtitle, onBack, onLogoPress, backLabel = 'Back', showLegal = true, welcome = false }) {
  const { theme } = useTheme();
  const dark = isDarkTheme(theme);
  const [viewport, setViewport] = useState({ width: 1000, height: 700 });
  const wide = viewport.width >= 980;
  const ink = dark ? '#F5F5F7' : '#1D1D1F';
  const muted = dark ? '#A1A1A6' : '#6E6E73';
  const paper = dark ? '#111113' : '#FFFFFF';
  const surface = dark ? '#232326' : '#F5F5F7';
  const border = dark ? '#3A3A3D' : '#DEDEE3';

  return <View onLayout={({ nativeEvent: { layout: { width, height } } }) => setViewport(previous => previous.width === width && previous.height === height ? previous : { width, height })}
    style={[styles.root, { backgroundColor: paper }]}>
    <View style={styles.topbar}>
      {onLogoPress ? <Pressable onPress={onLogoPress} accessibilityRole="button" accessibilityLabel="Gab AI home" style={styles.brand}><Text weight="bold" style={[styles.wordmark, { color: ink }]}>Gab AI</Text></Pressable>
        : <Text weight="bold" style={[styles.wordmark, { color: ink }]}>Gab AI</Text>}
      {onBack && <Pressable onPress={onBack} accessibilityRole="button" accessibilityLabel={backLabel}
        style={({ hovered, pressed }) => [styles.back, { backgroundColor: hovered || pressed ? surface : 'transparent' }]}>
        <Icon name="chevron-left" size={14} color={muted} /><Text size="s" style={{ color: muted }}>{backLabel}</Text>
      </Pressable>}
    </View>
    <ScrollView keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false} contentContainerStyle={[styles.content, !wide && styles.compactContent]}>
      <View style={[styles.columns, wide && styles.wideColumns]}>
        {wide && <View style={[styles.intro, { borderRightColor: border }]}>
          <View style={[styles.mark, { backgroundColor: surface }]} accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants"><Logo width={52} height={52} color={ink} /></View>
          <Text accessibilityRole="header" weight="medium" style={[styles.heroTitle, { color: ink }]}>AI that works.{'\n'}For you.</Text>
          <Text size="l" style={{ color: muted, lineHeight: 26 }}>Less busywork. More possibility.</Text>
          <View style={[styles.capabilities, { backgroundColor: surface }]}>
            {[
              ['comments', 'A fresh perspective', 'Think, ask, and explore with Chat.'],
              ['briefcase', 'A finished result', 'Research, write, and create with Work.'],
            ].map(([icon, heading, detail], index) => <View key={icon} style={[styles.capability, index > 0 && { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: border }]}>
              <Icon name={icon} size={22} color={ink} />
              <View style={{ flex: 1, gap: 5 }}><Text size="m" weight="bold" style={{ color: ink }}>{heading}</Text><Text size="s" style={{ color: muted, lineHeight: 20 }}>{detail}</Text></View>
            </View>)}
          </View>
        </View>}
        <View style={[styles.panel, !wide && { paddingLeft: 0 }]}>
          <View style={styles.form}>
            <View style={styles.header}>
              {!wide && <View style={[styles.smallMark, { backgroundColor: surface }]} accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants"><Logo width={36} height={36} color={ink} /></View>}
              {welcome && <Text size="xs" weight="bold" style={[styles.eyebrow, { color: muted }]}>WELCOME TO GAB AI</Text>}
              {!!title && <Text accessibilityRole="header" weight="bold" style={[styles.title, { color: ink }]}>{title}</Text>}
              {!!subtitle && <Text size="m" style={{ color: muted, lineHeight: 23 }}>{subtitle}</Text>}
            </View>
            {children}
            {showLegal && <View style={styles.legal}>
              {!welcome && <Text size="xs" style={{ color: muted, textAlign: 'center' }}>By continuing, you agree to our</Text>}
              <View style={styles.legalLinks}>
                {[['Terms of Service', 'terms-of-service'], ['Privacy Policy', 'privacy-policy']].map(([label, path]) => Platform.OS === 'web'
                  ? <Link key={path} href={`/${path}`} asChild><Pressable accessibilityRole="link" style={styles.legalLink}><Text size="xs" style={{ color: muted }}>{label}</Text></Pressable></Link>
                  : <Pressable key={path} accessibilityRole="link" onPress={() => Linking.openURL(`https://gab.ai/${path}`)} style={styles.legalLink}><Text size="xs" style={{ color: muted }}>{label}</Text></Pressable>)}
              </View>
            </View>}
          </View>
        </View>
      </View>
    </ScrollView>
  </View>;
}

const styles = StyleSheet.create({
  root: { flex: 1, minHeight: 0, width: '100%' },
  topbar: { minHeight: 80, paddingHorizontal: 32, paddingVertical: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
  brand: { minHeight: 44, justifyContent: 'center' },
  wordmark: { fontSize: 22, letterSpacing: -0.6 },
  back: { minHeight: 44, flexDirection: 'row', gap: 8, alignItems: 'center', paddingHorizontal: 14, borderRadius: 12 },
  content: { flexGrow: 1, alignItems: 'center', justifyContent: 'center', padding: 32, paddingBottom: 56 },
  compactContent: { paddingHorizontal: 24, paddingTop: 20, paddingBottom: 32 },
  columns: { width: '100%', maxWidth: 1080 },
  wideColumns: { flexDirection: 'row', alignItems: 'center' },
  intro: { width: '50%', paddingRight: 64, paddingVertical: 16, borderRightWidth: StyleSheet.hairlineWidth, gap: 24 },
  mark: { width: 108, height: 108, borderRadius: 32, alignItems: 'center', justifyContent: 'center' },
  smallMark: { width: 72, height: 72, borderRadius: 22, alignItems: 'center', justifyContent: 'center', marginBottom: 12 },
  heroTitle: { fontSize: 52, lineHeight: 58, letterSpacing: -1.8 },
  capabilities: { borderRadius: 22, paddingHorizontal: 22, marginTop: 8 },
  capability: { flexDirection: 'row', alignItems: 'center', gap: 16, paddingVertical: 20 },
  panel: { flex: 1, minWidth: 0, paddingLeft: 64, alignItems: 'center' },
  form: { width: '100%', maxWidth: 400 },
  header: { gap: 12, marginBottom: 28 },
  eyebrow: { letterSpacing: 1.5, marginBottom: 4 },
  title: { fontSize: 34, lineHeight: 40, letterSpacing: -1 },
  legal: { marginTop: 24, alignItems: 'center', gap: 2 },
  legalLinks: { flexDirection: 'row', gap: 16, justifyContent: 'center', flexWrap: 'wrap' },
  legalLink: { minHeight: 44, justifyContent: 'center', paddingHorizontal: 4 },
});
